/**
 * CareModelTasksBlock — the physician's care-model tasks on the patient record (ELLMS, 23 Oct 2026). The care model is
 * infrastructure: the physician sees the patient's active care-model runs here and completes the review step a run is
 * waiting on, without opening the studio.
 *
 * One row per active run: the care model's name and the step it waits on. When that step is a Checklist, its items are
 * listed with WHY the run reached it (read from the run: the step before it, its outcome, and the bound inputs that had
 * no answer). Ticking an item calls the engine's checklist tick (the call the studio run view makes); the engine resumes
 * the run once every item is done, and the block reads the runs again.
 *
 * Rules held here:
 *   · Physician only (`isDoctor` and not allied health). Any other role: nothing renders and no request is made.
 *   · Fails closed (`tickBlock`): a step assigned to named people is read-only unless the physician is one of them; an
 *     unreadable assignment and a step with more than one item (engine defect: one tick moves it on) are read-only.
 *     An unassigned single-item step can be ticked (the engine lets anyone in the practice tick it, as the studio says).
 *   · The engine has no read of one item's state: an item reads done only when it was ticked from this browser.
 *   · Nothing renders while reading, with no active run, or with no patient: no empty card on the record.
 *   · A failed list read is one line in words, never "no tasks". A run whose detail cannot be read keeps its row.
 *   · A tick is one click, sent once, never retried on a guess. An item the engine cannot tick (empty or with "/")
 *     is shown, not offered.
 *
 * Look (showcase rules, Antonin): the runs are one DS `Table` list, so the bridge draws them as every list: 48 rows,
 * 16 cell sides, a hairline between rows, the grey-100 hover. A run's status is a glyph with its word (`GlyphText`, as
 * on Today), never a chip per row. Each row has one icon action (the shared `RowIconButton`) that opens the care plan.
 * A run waiting on a checklist has a detail row under it with the step, the "Why:" line and the items. Rows and cells
 * keep the DS `data-slot`s (the bridge's hooks); this file marks its own parts with `data-part`.
 *
 * Pieces: Signal DS Table, Checkbox, HelperText, Text role classes; StatusGlyph `GlyphText`; `RowIconButton`; Lucide
 * glyphs (PROD-1599); the record's OverviewBlock frame.
 */
import { ClipboardCheck, ExternalLink } from 'lucide-react';
import { Checkbox, HelperText, Table, TableBody, TableCell, TableRow, TEXT_ROLE_CLASSES } from '@scripsteam/scrips-signal-ds';
import { useAuthStore } from '@/auth/auth-store';
import { isAlliedHealth, isDoctor } from '@/auth/roles';
import { canTickLabel, describeTickError, ITEMS_UNREADABLE, reasonLine, tickBlock, type WaitingChecklist } from '@/patients/lib/care-model-tasks';
import { useCareModelRuns, useChecklistTick, type CareModelRun } from '@/patients/services/use-care-model-tasks';
import { RowIconButton } from '@/shared/ui/components/row-icon-button';
import { GlyphText } from '@/shared/ui/components/status-glyph';
import { SEMANTIC_GLYPH } from '@/shared/ui/components/status-glyph-tokens';
import { OverviewBlock } from './overview-block';

export const BLOCK_TITLE = 'Care model tasks';
export const LIST_ERROR = 'Could not load the care model runs. This is not an empty list: try again later.';
export const DETAIL_ERROR = 'The details of this run could not be read.';
export const WAITING_STATUS = 'Waiting on review';
export const ACTIVE_STATUS = 'Active';
/** Columns of a run row: name and step, status, the row action. The detail row spans them. */
const RUN_COLUMNS = 3;

export interface CareModelTasksBlockProps {
  patientId: string | null;
  /** Opens the record's Care plan page. */
  onOpenCarePlan: () => void;
}

export function CareModelTasksBlock({ patientId, onOpenCarePlan }: CareModelTasksBlockProps) {
  // Role 3 also holds allied-health accounts (tenant role 20, PROD-2040): they are not physicians.
  const physician = useAuthStore((s) => isDoctor(s.user) && !isAlliedHealth(s.user));
  const runs = useCareModelRuns(patientId, physician);
  const userId = useAuthStore((s) => s.user?.userId);
  const practitionerId = useAuthStore((s) => s.user?.practitionerId);
  const setupId = useAuthStore((s) => s.user?.id);

  if (!physician || !patientId || runs.isLoading || runs.isPending) return null;
  if (!runs.isError && (runs.data ?? []).length === 0) return null;

  // Only the reviews this physician can tick here count as "waiting on you".
  const waiting = (runs.data ?? []).filter((r) => r.checklist && tickBlock(r.checklist, [setupId, userId, practitionerId]) === null).length;
  return (
    <OverviewBlock
      title={BLOCK_TITLE}
      icon={<ClipboardCheck size={16} aria-hidden />}
      linkLabel="Care plan"
      onOpen={onOpenCarePlan}
      wide
    >
      <div data-slot="care-model-tasks" className="flex flex-col gap-2">
        {runs.isError ? (
          <p data-slot="overview-empty">{LIST_ERROR}</p>
        ) : (
          <>
            <p className={TEXT_ROLE_CLASSES.meta} data-slot="care-model-tasks-summary">
              {runs.data.length === 1 ? '1 active care model' : `${runs.data.length} active care models`}
              {waiting > 0 ? ` · ${waiting === 1 ? '1 review waiting on you' : `${waiting} reviews waiting on you`}` : ''}
            </p>
            <Table aria-label="Active care model runs">
              <TableBody>
                {runs.data.flatMap((run) => runRows(run, patientId, onOpenCarePlan))}
              </TableBody>
            </Table>
          </>
        )}
      </div>
    </OverviewBlock>
  );
}

/** What a run without a checklist says under its name: its current step, or why its detail is missing. A run waiting
 *  on a checklist names its step in the detail row instead. */
function stepLine(run: CareModelRun): string {
  const parts = [run.unreadable ? DETAIL_ERROR : null, run.currentStepName ? `Current step: ${run.currentStepName}` : run.unreadable ? null : 'Not waiting on a step.'];
  return parts.filter(Boolean).join(' ');
}

/** One 48 row per run (name and step, status glyph, open action), then the checklist detail row when it waits on one. */
function runRows(run: CareModelRun, patientId: string, onOpenCarePlan: () => void) {
  const name = run.modelName ?? 'Care model';
  const rows = [
    <TableRow key={run.enrolmentId} data-part="care-model-run" data-enrolment={run.enrolmentId}>
      <TableCell className="w-full whitespace-normal">
        <div className={TEXT_ROLE_CLASSES['body-emphasis']}>{name}</div>
        {run.checklist ? null : (
          <div className={TEXT_ROLE_CLASSES.meta} data-slot="care-model-run-step">
            {stepLine(run)}
          </div>
        )}
      </TableCell>
      <TableCell data-part="care-model-run-status">
        {run.checklist ? (
          <GlyphText kind={SEMANTIC_GLYPH.warning}>{WAITING_STATUS}</GlyphText>
        ) : (
          <GlyphText kind={SEMANTIC_GLYPH.info}>{ACTIVE_STATUS}</GlyphText>
        )}
      </TableCell>
      <TableCell className="text-right" data-part="care-model-run-actions">
        <RowIconButton label={`Open care plan for ${name}`} tooltip="Open care plan" onClick={onOpenCarePlan}>
          <ExternalLink className="size-4" aria-hidden />
        </RowIconButton>
      </TableCell>
    </TableRow>,
  ];
  if (run.checklist) {
    rows.push(
      <TableRow key={`${run.enrolmentId}-detail`} data-part="care-model-run-detail" data-enrolment={run.enrolmentId}>
        <TableCell colSpan={RUN_COLUMNS} className="whitespace-normal">
          <div className="py-3">
            <ChecklistTask key={`${run.checklist.instanceId}|${run.checklist.activityId}`} step={run.checklist} patientId={patientId} />
          </div>
        </TableCell>
      </TableRow>,
    );
  }
  return rows;
}

export function ChecklistTask({ step, patientId }: { step: WaitingChecklist; patientId: string }) {
  const { ticked, tick, pendingLabel, error } = useChecklistTick(patientId, step);
  const userId = useAuthStore((s) => s.user?.userId);
  const practitionerId = useAuthStore((s) => s.user?.practitionerId);
  const setupId = useAuthStore((s) => s.user?.id);
  const blocked = tickBlock(step, [setupId, userId, practitionerId]);
  const allowed = blocked === null;
  return (
    <div data-slot="care-model-checklist" className="flex flex-col gap-2 pl-1">
      <p className={TEXT_ROLE_CLASSES.body} data-slot="care-model-step">
        {step.title}
      </p>
      {step.reason ? (
        <p className={TEXT_ROLE_CLASSES.meta} data-slot="care-model-why">
          Why: {reasonLine(step.reason)}
        </p>
      ) : null}
      {step.items === null ? (
        <p className={TEXT_ROLE_CLASSES.meta}>{ITEMS_UNREADABLE}</p>
      ) : (
        <ul className="flex flex-col gap-1" aria-label={`Items of ${step.title}`}>
          {step.items.map((label) => {
            const done = ticked.has(label);
            const tickable = canTickLabel(label);
            return (
              <li key={label} data-slot="care-model-item" data-done={done ? 'true' : undefined}>
                <Checkbox
                  label={label.trim() || 'Unnamed item'}
                  checked={done}
                  disabled={done || !tickable || !allowed || pendingLabel !== null}
                  onCheckedChange={(checked) => {
                    if (checked && !done && tickable && allowed) tick(label);
                  }}
                />
              </li>
            );
          })}
        </ul>
      )}
      {blocked && blocked !== ITEMS_UNREADABLE ? <p className={TEXT_ROLE_CLASSES.meta}>{blocked}</p> : null}
      {pendingLabel !== null ? <p className={TEXT_ROLE_CLASSES.meta}>Marking done…</p> : null}
      {error ? (
        <HelperText tone="error">
          {describeTickError(error)}
        </HelperText>
      ) : null}
    </div>
  );
}
